import React, { useState, useRef, useEffect, useContext, useMemo, useCallback } from 'react';
import { ShopContext } from '../context/ShopContext';
import './VideoShowcase.css';

const DEFAULT_FALLBACK_VIDEOS = [
  {
    id: 1,
    title: "The Jersey Collection",
    subtitle: "Heavyweight Boxy Streetwear",
    src: "https://cdn.coverr.co/videos/coverr-fashion-model-in-streetwear-5421/1080p.mp4",
    poster: "/images/collection-classic.webp",
    quality: "1080p Full HD"
  },
  {
    id: 2,
    title: "The Women Collection",
    subtitle: "Minimalist Modern Cut",
    src: "https://cdn.coverr.co/videos/coverr-fashion-portrait-of-a-model-4768/1080p.mp4",
    poster: "/images/collection-studio.webp",
    quality: "1080p Full HD"
  },
  {
    id: 3,
    title: "The Men Collection",
    subtitle: "Urban Statement Aesthetics",
    src: "https://cdn.coverr.co/videos/coverr-young-stylish-man-walking-in-the-city-4521/1080p.mp4",
    poster: "/images/collection-statement.webp",
    quality: "1080p Full HD"
  },
  {
    id: 4,
    title: "Heavyweight Hoodie Drape",
    subtitle: "450 GSM French Terry",
    src: "https://cdn.coverr.co/videos/coverr-model-wearing-a-hoodie-and-sunglasses-4821/1080p.mp4",
    poster: "/images/hero-slide-2.webp",
    quality: "1080p Full HD"
  },
  {
    id: 5,
    title: "Studio Craft & Texture",
    subtitle: "Bespoke Finishing Details",
    src: "https://cdn.coverr.co/videos/coverr-a-stylish-model-posing-in-a-studio-7935/1080p.mp4",
    poster: "/images/hero-slide-1.webp",
    quality: "1080p Full HD"
  },
  {
    id: 6,
    title: "Urban Leather & Denim",
    subtitle: "Signature Tailoring",
    src: "https://cdn.coverr.co/videos/coverr-stylish-young-woman-in-a-leather-jacket-4835/1080p.mp4",
    poster: "/images/hero-bg.webp",
    quality: "1080p Full HD"
  }
];

const VideoShowcase = () => {
  const { videos } = useContext(ShopContext);
  const [activeModalVideo, setActiveModalVideo] = useState(null);
  const [modalIsPlaying, setModalIsPlaying] = useState(true);
  const [modalIsMuted, setModalIsMuted] = useState(false);
  const [modalVolume, setModalVolume] = useState(0.9);
  const [modalProgress, setModalProgress] = useState(0);
  const [modalCurrentTime, setModalCurrentTime] = useState(0);
  const [modalDuration, setModalDuration] = useState(0);
  const [modalIsFullscreen, setModalIsFullscreen] = useState(false);
  const [controlsVisible, setControlsVisible] = useState(true);

  const videoRefs = useRef({});
  const sectionRef = useRef(null);
  const modalVideoRef = useRef(null);
  const modalContainerRef = useRef(null);
  const hideControlsTimerRef = useRef(null);
  const [isInView, setIsInView] = useState(false);

  // IntersectionObserver to only load video media when user scrolls near the section
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsInView(true);
          }
        });
      },
      { rootMargin: '250px 0px 250px 0px', threshold: 0.01 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Display all videos saved in database/context, or fallback defaults if empty
  const displayVideos = useMemo(() => {
    if (Array.isArray(videos) && videos.length > 0) {
      return videos;
    }
    return DEFAULT_FALLBACK_VIDEOS.slice(0, 5);
  }, [videos]);

  // Play video clips automatically when section is in view
  useEffect(() => {
    if (!isInView) return;

    displayVideos.forEach(clip => {
      const vid = videoRefs.current[clip.id];
      if (vid) {
        vid.muted = true;
        const playPromise = vid.play();
        if (playPromise !== undefined) {
          playPromise.catch(() => {});
        }
      }
    });
  }, [isInView, displayVideos]);

  const openFullHdTheater = (clip) => {
    setActiveModalVideo(clip);
    setModalIsPlaying(true);
    setModalIsMuted(false);
    setModalProgress(0);
    setModalCurrentTime(0);
    setModalDuration(0);
  };

  const closeFullHdTheater = useCallback(() => {
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
    }
    setActiveModalVideo(null);
  }, []);

  const handlePrevVideo = useCallback((e) => {
    if (e) e.stopPropagation();
    if (!activeModalVideo) return;
    const currentIndex = displayVideos.findIndex(v => v.id === activeModalVideo.id);
    const prevIndex = (currentIndex - 1 + displayVideos.length) % displayVideos.length;
    setActiveModalVideo(displayVideos[prevIndex]);
    setModalProgress(0);
  }, [activeModalVideo, displayVideos]);

  const handleNextVideo = useCallback((e) => {
    if (e) e.stopPropagation();
    if (!activeModalVideo) return;
    const currentIndex = displayVideos.findIndex(v => v.id === activeModalVideo.id);
    const nextIndex = (currentIndex + 1) % displayVideos.length;
    setActiveModalVideo(displayVideos[nextIndex]);
    setModalProgress(0);
  }, [activeModalVideo, displayVideos]);

  // Modal Video Time Update
  const handleTimeUpdate = () => {
    if (modalVideoRef.current) {
      const current = modalVideoRef.current.currentTime;
      const duration = modalVideoRef.current.duration || 1;
      setModalCurrentTime(current);
      setModalDuration(duration);
      setModalProgress((current / duration) * 100);
    }
  };

  // Seek bar click
  const handleSeek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const pos = (e.clientX - rect.left) / rect.width;
    if (modalVideoRef.current && modalDuration) {
      modalVideoRef.current.currentTime = pos * modalDuration;
    }
  };

  // Toggle modal play / pause
  const toggleModalPlay = () => {
    if (modalVideoRef.current) {
      if (modalVideoRef.current.paused) {
        modalVideoRef.current.play();
        setModalIsPlaying(true);
      } else {
        modalVideoRef.current.pause();
        setModalIsPlaying(false);
      }
    }
  };

  // Toggle modal mute
  const toggleModalMute = () => {
    if (modalVideoRef.current) {
      modalVideoRef.current.muted = !modalIsMuted;
      setModalIsMuted(!modalIsMuted);
    }
  };

  // Change modal volume
  const handleVolumeChange = (e) => {
    const val = parseFloat(e.target.value);
    setModalVolume(val);
    if (modalVideoRef.current) {
      modalVideoRef.current.volume = val;
      modalVideoRef.current.muted = val === 0;
      setModalIsMuted(val === 0);
    }
  };

  // Fullscreen toggle
  const toggleFullscreen = () => {
    if (!modalContainerRef.current) return;
    if (!document.fullscreenElement) {
      modalContainerRef.current.requestFullscreen().then(() => {
        setModalIsFullscreen(true);
      }).catch(err => console.log('Fullscreen note:', err.message));
    } else {
      document.exitFullscreen().then(() => {
        setModalIsFullscreen(false);
      }).catch(err => console.log('Fullscreen exit note:', err.message));
    }
  };

  // Mouse activity auto-hide controls in theater mode
  const handleMouseMove = () => {
    setControlsVisible(true);
    if (hideControlsTimerRef.current) {
      clearTimeout(hideControlsTimerRef.current);
    }
    hideControlsTimerRef.current = setTimeout(() => {
      if (modalIsPlaying) {
        setControlsVisible(false);
      }
    }, 2800);
  };

  // Keyboard shortcut listener
  useEffect(() => {
    if (!activeModalVideo) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        closeFullHdTheater();
      } else if (e.key === ' ' || e.code === 'Space') {
        e.preventDefault();
        toggleModalPlay();
      } else if (e.key === 'm' || e.key === 'M') {
        toggleModalMute();
      } else if (e.key === 'f' || e.key === 'F') {
        toggleFullscreen();
      } else if (e.key === 'ArrowRight') {
        if (modalVideoRef.current) modalVideoRef.current.currentTime += 5;
      } else if (e.key === 'ArrowLeft') {
        if (modalVideoRef.current) modalVideoRef.current.currentTime -= 5;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeModalVideo, modalIsPlaying, modalIsMuted, closeFullHdTheater]);

  const formatTime = (secs) => {
    if (isNaN(secs)) return '0:00';
    const m = Math.floor(secs / 60);
    const s = Math.floor(secs % 60);
    return `${m}:${s < 10 ? '0' : ''}${s}`;
  };

  return (
    <section ref={sectionRef} className="video-showcase-section container reveal-el">
      {/* 5 Video Clips Display Grid - Pure Clean Edge-to-Edge Videos Only */}
      <div className="video-clips-reel-grid">
        {displayVideos.map((clip) => {
          return (
            <div 
              key={clip.id}
              className="video-clip-card clean-video-only"
              onClick={() => openFullHdTheater(clip)}
            >
              <div className="video-clip-media-wrapper">
                <video
                  ref={el => (videoRefs.current[clip.id] = el)}
                  src={isInView ? clip.src : undefined}
                  poster={clip.poster}
                  autoPlay={isInView}
                  loop
                  muted
                  playsInline
                  preload={isInView ? "metadata" : "none"}
                  className="clip-video-element"
                  width="360"
                  height="640"
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* ============================================================ */}
      {/* 🎬 1080p FULL HD THEATER LIGHTBOX MODAL                      */}
      {/* ============================================================ */}
      {activeModalVideo && (
        <div 
          className="hd-theater-backdrop animate-fade-in"
          onClick={closeFullHdTheater}
          onMouseMove={handleMouseMove}
        >
          <div 
            ref={modalContainerRef}
            className="hd-theater-container"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Top Bar with HD indicator and Close button */}
            <div className={`hd-theater-top-bar ${controlsVisible ? 'visible' : 'hidden'}`}>
              <div className="hd-video-title-group">
                <div className="hd-res-badge">
                  <span className="hd-res-pulse"></span>
                  <span className="hd-res-name">1080p FULL HD</span>
                </div>
                <div className="hd-video-headings">
                  <h3 className="hd-modal-title">{activeModalVideo.title}</h3>
                  {activeModalVideo.subtitle && (
                    <span className="hd-modal-subtitle">{activeModalVideo.subtitle}</span>
                  )}
                </div>
              </div>

              <div className="hd-theater-top-actions">
                <button 
                  className="hd-icon-btn" 
                  onClick={toggleFullscreen} 
                  title={modalIsFullscreen ? "Exit Fullscreen (F)" : "Fullscreen (F)"}
                >
                  {modalIsFullscreen ? (
                    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3"/>
                    </svg>
                  ) : (
                    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/>
                    </svg>
                  )}
                </button>
                <button 
                  className="hd-icon-btn hd-close-btn" 
                  onClick={closeFullHdTheater} 
                  title="Close Full HD Theater (Esc)"
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Main Video Viewport */}
            <div className="hd-video-viewport" onClick={toggleModalPlay}>
              <video
                ref={modalVideoRef}
                src={activeModalVideo.src}
                poster={activeModalVideo.poster}
                autoPlay
                loop
                playsInline
                preload="auto"
                onTimeUpdate={handleTimeUpdate}
                onLoadedMetadata={handleTimeUpdate}
                className="hd-modal-video-element"
              />

              {/* Central Play/Pause Flash Overlay */}
              {!modalIsPlaying && (
                <div className="hd-paused-overlay">
                  <div className="hd-big-play-btn">
                    <svg viewBox="0 0 24 24" width="36" height="36" fill="currentColor">
                      <polygon points="6 4 20 12 6 20 6 4" />
                    </svg>
                  </div>
                </div>
              )}

              {/* Navigation Arrows for Next / Prev */}
              <button 
                className="hd-nav-arrow hd-nav-prev" 
                onClick={handlePrevVideo}
                title="Previous Video"
                aria-label="Previous Video"
              >
                ‹
              </button>
              <button 
                className="hd-nav-arrow hd-nav-next" 
                onClick={handleNextVideo}
                title="Next Video"
                aria-label="Next Video"
              >
                ›
              </button>
            </div>

            {/* Bottom Interactive Control Bar */}
            <div className={`hd-theater-bottom-bar ${controlsVisible ? 'visible' : 'hidden'}`}>
              {/* Progress Scrubber */}
              <div className="hd-scrubber-track" onClick={handleSeek}>
                <div className="hd-scrubber-buffered" style={{ width: '100%' }}></div>
                <div className="hd-scrubber-progress" style={{ width: `${modalProgress}%` }}>
                  <div className="hd-scrubber-handle"></div>
                </div>
              </div>

              <div className="hd-controls-row">
                {/* Left Controls */}
                <div className="hd-controls-left">
                  <button 
                    className="hd-control-btn" 
                    onClick={toggleModalPlay}
                    title={modalIsPlaying ? "Pause (Space)" : "Play (Space)"}
                  >
                    {modalIsPlaying ? (
                      <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
                        <rect x="6" y="4" width="4" height="16"></rect>
                        <rect x="14" y="4" width="4" height="16"></rect>
                      </svg>
                    ) : (
                      <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
                        <polygon points="5 3 19 12 5 21 5 3" />
                      </svg>
                    )}
                  </button>

                  {/* Volume Control */}
                  <div className="hd-volume-group">
                    <button className="hd-control-btn" onClick={toggleModalMute} title="Mute/Unmute (M)">
                      {modalIsMuted || modalVolume === 0 ? '🔇' : modalVolume > 0.5 ? '🔊' : '🔉'}
                    </button>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.05"
                      value={modalIsMuted ? 0 : modalVolume}
                      onChange={handleVolumeChange}
                      className="hd-volume-slider"
                      title="Adjust Volume"
                    />
                  </div>

                  {/* Time Display */}
                  <span className="hd-time-display">
                    {formatTime(modalCurrentTime)} / {formatTime(modalDuration)}
                  </span>
                </div>

                {/* Right Controls */}
                <div className="hd-controls-right">
                  <div className="hd-quality-tag" title="Native High Bitrate 1080p Stream">
                    <span className="hd-check">✓</span> 1080p HD • 60 FPS
                  </div>

                  <button 
                    className="hd-control-btn" 
                    onClick={toggleFullscreen}
                    title="Toggle Fullscreen (F)"
                  >
                    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default VideoShowcase;
